import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ChevronRight, ChevronDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { formatDate, formatCurrency } from "@/lib/utils";
import { AllContactsTab } from "@/components/customers/all-contacts-tab";
import { CommunicationHistoryTab } from "@/components/customers/communication-history-tab";
import { CustomerMetricCards } from "@/components/customers/customer-metric-cards";
import { mockUsers } from "@/lib/mock-users";
import { useCustomerDetails, useCustomerInvoices, useCustomerPayments } from "@/hooks/useCustomerDetailData";
import type { CustomerDetailUI, InvoiceUI, PaymentUI } from "@/types/ui/customer-detail";

type Customer = CustomerDetailUI;

// Types
interface Invoice {
  id: string;
  date: string;
  dueDate: string;
  amount: number;
  status: "overdue" | "outstanding" | "paid";
}

interface Payment {
  id: string;
  date: string;
  amount: number;
  method: string;
  reference: string;
  allocations: {
    invoiceId: string;
    invoiceDate: string;
    amount: number;
  }[];
}

// Mock Invoices
const mockInvoices: Record<string, Invoice[]> = {
  "1": [
    {
      id: "INV-001",
      date: "2023-11-01",
      dueDate: "2023-12-01",
      amount: 76000,
      status: "overdue",
    },
    {
      id: "INV-002",
      date: "2023-11-15",
      dueDate: "2023-12-15",
      amount: 68000,
      status: "overdue",
    },
    {
      id: "INV-003",
      date: "2023-12-01",
      dueDate: "2023-12-31",
      amount: 70000,
      status: "overdue",
    },
  ],
};

// Mock Payments
const mockPayments: Record<string, Payment[]> = {
  "1": [
    {
      id: "PAY-001",
      date: "2023-11-15",
      amount: 50000,
      method: "Wire Transfer",
      reference: "WT-2023-001",
      allocations: [
        { invoiceId: "INV-001", invoiceDate: "2023-10-01", amount: 30000 },
        { invoiceId: "INV-002", invoiceDate: "2023-10-15", amount: 20000 },
      ],
    },
    {
      id: "PAY-002",
      date: "2023-10-20",
      amount: 35000,
      method: "ACH",
      reference: "ACH-2023-002",
      allocations: [{ invoiceId: "INV-003", invoiceDate: "2023-09-01", amount: 35000 }],
    },
  ],
};

// Collection Strategies
const collectionStrategies = [
  { id: "1", name: "Standard Collection Flow", status: "active" },
  { id: "2", name: "Intensive Collection", status: "active" },
  { id: "3", name: "Gentle Reminder Flow", status: "inactive" },
];

export function CustomerDetailPage() {
  const navigate = useNavigate();
  const { customerId } = useParams<{ customerId: string }>();
  const location = useLocation();
  
  // Fetch customer data using mode-aware hooks
  const { data: customer, isLoading, error: customerError } = useCustomerDetails(customerId);
  const { data: invoices = [] } = useCustomerInvoices(customerId);
  const { data: payments = [] } = useCustomerPayments(customerId);
  
  const [editedCollectionStrategyId, setEditedCollectionStrategyId] = useState("");
  const [editedCreditControllerId, setEditedCreditControllerId] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [activeTransactionTab, setActiveTransactionTab] = useState("outstanding");
  const [expandedPayments, setExpandedPayments] = useState<string[]>([]);

  // Update collection strategy when customer data loads
  useEffect(() => {
    try {
      if (customer) {
        setEditedCollectionStrategyId(customer.collectionStrategyId || "none");
        setEditedCreditControllerId(customer.creditControllerId || "none");
      }
    } catch (error) {
      console.error("Error setting collection strategy:", error);
    }
  }, [customer]);
  
  const error = customerError ? (customerError instanceof Error ? customerError.message : "Failed to load customer") : null;

  // Risk badge helper
  const getRiskBadge = (risk: string) => {
    switch (risk) {
      case "high":
        return <Badge variant="destructive">High Risk</Badge>;
      case "medium":
        return (
          <Badge className="bg-amber-500 text-white hover:bg-amber-600">Medium Risk</Badge>
        );
      case "low":
        return <Badge className="bg-green-600 text-white hover:bg-green-700">Low Risk</Badge>;
      default:
        return <Badge variant="secondary">Unknown Risk</Badge>;
    }
  };

  // Handle save collection settings
  const handleSaveCollectionSettings = () => {
    if (!customer) return;

    try {
      // Note: In a real implementation, this would call an API to update the customer
      // For now, just show success message
      toast.success("Collection settings updated successfully");
    } catch (error) {
      console.error("Error saving collection settings:", error);
      toast.error("Failed to save collection settings");
    }
  };

  // Calculate aging
  const calculateAging = (customer: Customer) => {
    const today = new Date();

    const aging = {
      current: 0,
      days1_30: 0,
      days31_60: 0,
      days61_90: 0,
      days90Plus: 0,
    };

    invoices.forEach((invoice) => {
      if (invoice.status === "paid") return;

      const dueDate = new Date(invoice.dueDate);
      const daysOverdue = Math.floor(
        (today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)
      );

      if (daysOverdue < 0) aging.current += invoice.amount;
      else if (daysOverdue <= 30) aging.days1_30 += invoice.amount;
      else if (daysOverdue <= 60) aging.days31_60 += invoice.amount;
      else if (daysOverdue <= 90) aging.days61_90 += invoice.amount;
      else aging.days90Plus += invoice.amount;
    });

    return aging;
  };

  // Toggle payment expansion
  const togglePaymentExpansion = (paymentId: string) => {
    if (expandedPayments.includes(paymentId)) {
      setExpandedPayments(expandedPayments.filter((id) => id !== paymentId));
    } else {
      setExpandedPayments([...expandedPayments, paymentId]);
    }
  };

  console.log("Rendering CustomerDetailPage - customer state:", customer);
  console.log("isLoading:", isLoading, "error:", error);

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex flex-col gap-6 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-lg text-muted-foreground">Loading customer details...</p>
            <p className="text-sm text-muted-foreground mt-2">Customer ID: {customerId}</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex flex-col gap-6 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-red-600 mb-2">Error Loading Customer</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <p className="text-sm text-muted-foreground mb-4">Customer ID: {customerId}</p>
            <Button onClick={() => navigate("/customers")}>
              Back to Customers
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  // Show not found state
  if (!customer) {
    console.log("Customer is null - showing not found message");
    return (
      <div className="flex flex-col gap-6 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Customer Not Found</h2>
            <p className="text-muted-foreground mb-2">
              The customer you're looking for doesn't exist or couldn't be loaded.
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              Customer ID: {customerId || "No ID provided"}
            </p>
            <Button onClick={() => navigate("/customers")}>
              Back to Customers
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  console.log("Customer found - rendering detail page for:", customer.name);

  // Main render - wrapped in try-catch for safety
  try {
    return (
    <div className="flex flex-col gap-6 p-6">
      {/* Breadcrumb Navigation */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <button
          onClick={() => navigate("/customers")}
          className="hover:text-foreground transition-colors"
        >
          Customers
        </button>
        <ChevronRight className="h-4 w-4" />
        <span className="text-foreground font-medium">{customer.name}</span>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-3xl font-bold">{customer.name}</h1>
            <p className="text-muted-foreground">{customer.accountNumber}</p>
          </div>
          {getRiskBadge(customer.risk)}
        </div>
        <Button variant="outline" onClick={() => navigate("/customers")}>
          Back to Customers
        </Button>
      </div>

      {/* Metric Cards */}
      <CustomerMetricCards
        customer={customer}
        invoices={invoices}
        payments={payments.map((p) => ({
          id: p.id,
          date: p.date,
          amount: p.amount,
          invoiceDate: p.allocations[0]?.invoiceDate,
        }))}
        onCardClick={(action) => {
          switch (action) {
            case "outstanding":
              setActiveTab("invoices");
              setActiveTransactionTab("outstanding");
              break;
            case "overdue":
              setActiveTab("invoices");
              setActiveTransactionTab("outstanding");
              break;
            case "credit":
              setActiveTab("financial");
              break;
            case "performance":
              setActiveTab("invoices");
              setActiveTransactionTab("payment-history");
              break;
          }
        }}
      />

      {/* Customer Details Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="overview">
            Overview
          </TabsTrigger>
          <TabsTrigger value="financial">
            Financial
          </TabsTrigger>
          <TabsTrigger value="ageing">
            Ageing
          </TabsTrigger>
          <TabsTrigger value="invoices">
            Transactions
          </TabsTrigger>
          <TabsTrigger value="communications">
            Communications
          </TabsTrigger>
          <TabsTrigger value="contacts">
            Contacts
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Account Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                  {/* Left Column - Existing Fields */}
                  <div className="space-y-2">
                    <div>
                      <Label className="text-muted-foreground">Account Number</Label>
                      <p className="font-medium">{customer.accountNumber}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Customer Name</Label>
                      <p className="font-medium">{customer.name}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Risk Level</Label>
                      <div className="mt-1">{getRiskBadge(customer.risk)}</div>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Currency</Label>
                      <p className="font-medium">{customer.currency}</p>
                    </div>
                  </div>

                  {/* Right Column - New ERP Fields */}
                  <div className="space-y-2">
                    <div>
                      <Label className="text-muted-foreground">On Stop</Label>
                      <div className="mt-1">
                        {customer.onStop !== undefined ? (
                          <Badge
                            className={
                              customer.onStop
                                ? "bg-red-600 text-white hover:bg-red-700"
                                : "bg-green-600 text-white hover:bg-green-700"
                            }
                          >
                            {customer.onStop ? "Yes" : "No"}
                          </Badge>
                        ) : (
                          <span className="text-sm text-muted-foreground">Not Available</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Status</Label>
                      <div className="mt-1">
                        {customer.status ? (
                          <Badge
                            className={
                              customer.status === "Active"
                                ? "bg-green-600 text-white hover:bg-green-700"
                                : "bg-gray-500 text-white hover:bg-gray-600"
                            }
                          >
                            {customer.status}
                          </Badge>
                        ) : (
                          <span className="text-sm text-muted-foreground">Not Available</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <p className="font-medium">{customer.contact}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Phone</Label>
                  <p className="font-medium">{customer.phone}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Address</Label>
                  <p className="font-medium">{customer.address}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Collection Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-end gap-4">
                <div className="flex-1">
                  <Label>Assigned Strategy</Label>
                  <Select
                    value={editedCollectionStrategyId}
                    onValueChange={setEditedCollectionStrategyId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select strategy" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {collectionStrategies
                        .filter((s) => s.status === "active")
                        .map((strategy) => (
                          <SelectItem key={strategy.id} value={strategy.id}>
                            {strategy.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <Label>Credit Controller</Label>
                  <Select
                    value={editedCreditControllerId}
                    onValueChange={setEditedCreditControllerId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select user" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {mockUsers
                        .filter((u) => u.status === "active")
                        .map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.firstName} {user.lastName}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={handleSaveCollectionSettings}
                  disabled={
                    editedCollectionStrategyId === (customer.collectionStrategyId || "none") &&
                    editedCreditControllerId === (customer.creditControllerId || "none")
                  }
                >
                  Save
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Category Keys</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                <div>
                  <Label className="text-muted-foreground">
                    {customer.userSortKey1Label || "User Sort Key 1"}
                  </Label>
                  <p className="font-medium">
                    {customer.userSortKey1 || <span className="text-sm text-muted-foreground">Not Available</span>}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">
                    {customer.userSortKey2Label || "User Sort Key 2"}
                  </Label>
                  <p className="font-medium">
                    {customer.userSortKey2 || <span className="text-sm text-muted-foreground">Not Available</span>}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">
                    {customer.userSortKey3Label || "User Sort Key 3"}
                  </Label>
                  <p className="font-medium">
                    {customer.userSortKey3 || <span className="text-sm text-muted-foreground">Not Available</span>}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">
                    {customer.userSortKey4Label || "User Sort Key 4"}
                  </Label>
                  <p className="font-medium">
                    {customer.userSortKey4 || <span className="text-sm text-muted-foreground">Not Available</span>}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Financial Tab */}
        <TabsContent value="financial" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Credit Limit</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">
                  {formatCurrency(customer.creditLimit, customer.currency)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Available Credit</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-green-600">
                  {formatCurrency(customer.creditLimit - customer.outstanding, customer.currency)}
                </p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Payment Terms & Performance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <Label className="text-muted-foreground">Payment Terms</Label>
                  <p className="text-lg font-medium">{customer.paymentTerms}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Due Days</Label>
                  <p className="text-lg font-medium">
                    {customer.dueDays !== undefined ? (
                      customer.dueDays
                    ) : (
                      <span className="text-sm text-muted-foreground">Not Available</span>
                    )}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Due Days Type</Label>
                  <p className="text-lg font-medium">
                    {customer.dueDaysType || (
                      <span className="text-sm text-muted-foreground">Not Available</span>
                    )}
                  </p>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <Label className="text-muted-foreground">Days Sales Outstanding</Label>
                  <p className="text-lg font-medium">{customer.dso} days</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Risk Level</Label>
                  <div className="mt-1">{getRiskBadge(customer.risk)}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Credit Utilization</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>
                  {formatCurrency(customer.outstanding, customer.currency)} of{" "}
                  {formatCurrency(customer.creditLimit, customer.currency)}
                </span>
                <span className="font-medium">
                  {Math.round((customer.outstanding / customer.creditLimit) * 100)}%
                </span>
              </div>
              <Progress value={(customer.outstanding / customer.creditLimit) * 100} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Ageing Tab */}
        <TabsContent value="ageing" className="space-y-4">
          {(() => {
            const aging = calculateAging(customer);
            const total =
              aging.current + aging.days1_30 + aging.days31_60 + aging.days61_90 + aging.days90Plus;
            const overdue = aging.days1_30 + aging.days31_60 + aging.days61_90 + aging.days90Plus;

            return (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle>Aging Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Period</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead className="text-right">Percentage</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell>Current (Not Due)</TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(aging.current, customer.currency)}
                          </TableCell>
                          <TableCell className="text-right">
                            {total > 0 ? Math.round((aging.current / total) * 100) : 0}%
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>1-30 Days</TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(aging.days1_30, customer.currency)}
                          </TableCell>
                          <TableCell className="text-right">
                            {total > 0 ? Math.round((aging.days1_30 / total) * 100) : 0}%
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>31-60 Days</TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(aging.days31_60, customer.currency)}
                          </TableCell>
                          <TableCell className="text-right">
                            {total > 0 ? Math.round((aging.days31_60 / total) * 100) : 0}%
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>61-90 Days</TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(aging.days61_90, customer.currency)}
                          </TableCell>
                          <TableCell className="text-right">
                            {total > 0 ? Math.round((aging.days61_90 / total) * 100) : 0}%
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="text-red-600">90+ Days</TableCell>
                          <TableCell className="text-right text-red-600">
                            {formatCurrency(aging.days90Plus, customer.currency)}
                          </TableCell>
                          <TableCell className="text-right text-red-600">
                            {total > 0 ? Math.round((aging.days90Plus / total) * 100) : 0}%
                          </TableCell>
                        </TableRow>
                        <TableRow className="font-bold">
                          <TableCell>Total Outstanding</TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(total, customer.currency)}
                          </TableCell>
                          <TableCell className="text-right">100%</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                <div className="grid gap-4 md:grid-cols-2">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Current & Not Overdue</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-green-600">
                        {formatCurrency(aging.current, customer.currency)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Overdue (30+ Days)</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-red-600">
                        {formatCurrency(overdue, customer.currency)}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </>
            );
          })()}
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="invoices" className="space-y-4">
          <Tabs value={activeTransactionTab} onValueChange={setActiveTransactionTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="outstanding">Outstanding</TabsTrigger>
              <TabsTrigger value="payment-history">Payment History</TabsTrigger>
            </TabsList>

            {/* Outstanding Sub-Tab */}
            <TabsContent value="outstanding" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Outstanding Invoices</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Invoice #</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Days Overdue</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoices
                        .filter((invoice) => invoice.status !== "paid")
                        .map((invoice) => {
                          const today = new Date();
                          const dueDate = new Date(invoice.dueDate);
                          const daysOverdue = Math.floor(
                            (today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)
                          );

                          return (
                            <TableRow key={invoice.id}>
                              <TableCell className="font-medium">{invoice.id}</TableCell>
                              <TableCell>{formatDate(invoice.date)}</TableCell>
                              <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                              <TableCell className="text-right">
                                {formatCurrency(invoice.amount, customer.currency)}
                              </TableCell>
                              <TableCell>
                                <Badge
                                  variant={invoice.status === "overdue" ? "destructive" : "secondary"}
                                >
                                  {invoice.status}
                                </Badge>
                              </TableCell>
                              <TableCell className={`text-right ${daysOverdue > 0 ? "text-red-600" : ""}`}>
                                {daysOverdue > 0 ? daysOverdue : 0}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Payment History Sub-Tab */}
            <TabsContent value="payment-history" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Payment History</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[50px]"></TableHead>
                        <TableHead>Payment ID</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Method</TableHead>
                        <TableHead>Reference</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payments.map((payment) => (
                        <>
                          <TableRow key={payment.id}>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => togglePaymentExpansion(payment.id)}
                              >
                                {expandedPayments.includes(payment.id) ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                              </Button>
                            </TableCell>
                            <TableCell className="font-medium">{payment.id}</TableCell>
                            <TableCell>{formatDate(payment.date)}</TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(payment.amount, customer.currency)}
                            </TableCell>
                            <TableCell>{payment.method}</TableCell>
                            <TableCell>{payment.reference}</TableCell>
                          </TableRow>
                          {expandedPayments.includes(payment.id) && (
                            <TableRow>
                              <TableCell colSpan={6} className="bg-muted/50">
                                <div className="p-4">
                                  <p className="mb-2 font-semibold">Invoice Allocations:</p>
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead>Invoice #</TableHead>
                                        <TableHead>Invoice Date</TableHead>
                                        <TableHead className="text-right">Allocated Amount</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {payment.allocations.map((allocation, idx) => (
                                        <TableRow key={idx}>
                                          <TableCell>{allocation.invoiceId}</TableCell>
                                          <TableCell>{formatDate(allocation.invoiceDate)}</TableCell>
                                          <TableCell className="text-right">
                                            {formatCurrency(allocation.amount, customer.currency)}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Payment Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label className="text-muted-foreground">Total Payments (Last 3 Months)</Label>
                      <p className="text-2xl font-bold">
                        {formatCurrency(
                          payments.reduce((sum, p) => sum + p.amount, 0),
                          customer.currency
                        )}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Last Payment Date</Label>
                      <p className="text-2xl font-bold">{formatDate(customer.lastPayment)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Communications Tab */}
        <TabsContent value="communications">
          <CommunicationHistoryTab customerId={customer.id} customerName={customer.name} />
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts">
          <AllContactsTab customerId={customer.id} customerName={customer.name} />
        </TabsContent>
      </Tabs>
    </div>
  );
  } catch (error) {
    console.error("Error rendering customer detail page:", error);
    return (
      <div className="flex flex-col gap-6 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-red-600 mb-2">Rendering Error</h2>
            <p className="text-muted-foreground mb-4">
              {error instanceof Error ? error.message : "An unknown error occurred while rendering the page"}
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              Customer: {customer?.name || "Unknown"} (ID: {customerId})
            </p>
            <Button onClick={() => navigate("/customers")}>
              Back to Customers
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
